<template>
  <b-modal
    id="fake-login-form"
    title="Login as"
    ref="fake-login-modal"
    button-size="sm"
    @hide="selectedUser = []"
  >
    <b-form-group label-for="fl-users">
      <div class="position-relative">
        <multi-select
          v-if="users.length"
          id="fl-users"
          btn-label="User"
          size="md"
          :options="{ multi: false }"
          :select-options="users"
          search
          v-model="selectedUser"
        >
          <template v-if="recentlyUsedUsers.length > 0" #beforeList="{ selectOption }">
            <div class="d-flex w-100 users-select__title border-top-0">Recently used</div>
            <ul class="select-list">
              <li
                v-for="user in recentlyUsedUsers"
                :key="user.id"
                class="select-item"
                @click="
                  selectOption({
                    id: user.id,
                    name: user.name,
                    fullName: user.fullName,
                  })
                "
              >
                <div class="d-flex flex-column gap-1 ms-1">
                  <span class="user-select__email">
                    {{ user.name }}
                  </span>
                  <span class="user-select__name">
                    {{ user.fullName }}
                  </span>
                </div>
              </li>
            </ul>
            <div class="d-flex w-100 users-select__title">All users</div>
          </template>
          <template #default="{ option }">
            <div class="d-flex flex-column gap-1 ms-1">
              <span class="user-select__email">
                {{ option.name }}
              </span>
              <span class="user-select__name">
                {{ option.fullName }}
              </span>
            </div>
          </template>
        </multi-select>
      </div>
    </b-form-group>

    <template #footer>
      <div class="fake-login-footer">
        <span />
        <div class="fake-login-footer__actions">
          <b-button size="sm" variant="outline-secondary" @click="$refs['fake-login-modal'].hide()">Cancel</b-button>
          <b-button size="sm" variant="outline-info" :disabled="!selectedUser.length" @click="fakeLoginInNewTab">
            Login in new tab
          </b-button>
          <b-button size="sm" variant="success" :disabled="!selectedUser.length" @click="fakeLogin">
            Login
          </b-button>
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { mapGetters } from 'vuex';

import * as config from '@/../config/index.js';
import { IMP_FP_KEY, IMP_TOKENS_KEY, isImpersonating, saveImpFp, saveImpTokens } from '@/auth/tabStorage';
import * as auth from '@/utils/auth';
import { useEventBus } from '@/utils/bus';
import { pickPreferredRole } from '@/utils/role';

const RECENTLY_USED_LIMIT = 3;

export default {
  props: {
    /**
     * When true, after successful fake login the page is reloaded in place
     * (same URL) and the impersonated user is auto-switched to the `advertiser`
     * role if available. Used by HeaderProfileMenu in the campaign editor so
     * the admin stays on the current campaign page.
     */
    stayOnCurrentUrl: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      users: [],
      selectedUser: [],
      recentlyUsedIds: [],
    };
  },

  computed: {
    // Resolved against the freshly loaded users, so banned or deleted ones drop out and names stay current.
    recentlyUsedUsers() {
      return this.recentlyUsedIds
        .map((id) => this.users.find((user) => user.id === id))
        .filter((user) => user && !user.isBanned)
        .slice(0, RECENTLY_USED_LIMIT);
    },
    ...mapGetters({
      user: 'user',
      availableRoles: 'availableRoles',
    }),
  },

  methods: {
    openForm() {
      // Users already loaded (on create or a previous open) show the recent ones at once, not after the slow refetch.
      this.recentlyUsedIds = this.users.length ? this.readRecentlyUsedIds() : [];
      this.fetchUsers({ useAdminCredentials: isImpersonating() }).then(() => {
        this.recentlyUsedIds = this.readRecentlyUsedIds();
      });
      this.$refs['fake-login-modal'].show();
    },

    getAdminUserData() {
      const fakeLogin = auth.readFakeLogin();
      const adminEmail = isImpersonating() ? fakeLogin.admin : auth.readUser() || this.user.email;
      const adminRole = isImpersonating() ? fakeLogin.adminRole : auth.readActiveRole();
      return {
        email: adminEmail,
        fp: auth.getFp(adminEmail),
        token: auth.readTokenForUser(adminEmail),
        role: adminRole,
        roleId: adminRole ? config.default.rolesIds[adminRole] : null,
      };
    },

    getAdminRequestConfig() {
      const admin = this.getAdminUserData();
      if (!admin.email || !admin.token || !admin.role || admin.roleId == null || !admin.fp) {
        this.$notify('alert', {
          text: 'Cannot impersonate: real admin session metadata is missing. Return to your account and login as this user again.',
          variant: 'danger',
          time: 5000,
        });
        return null;
      }
      return {
        headers: {
          Authorization: `Bearer ${admin.token}`,
          'X-ACTIVE-ROLE-ID': admin.roleId,
        },
        skipAuthRefresh: true,
      };
    },

    fetchUsers({ useAdminCredentials = false } = {}) {
      const params = { orderBy: 'created_at', order: 2, limit: 9999 };
      const adminRequestConfig = useAdminCredentials ? this.getAdminRequestConfig() : null;
      if (useAdminCredentials && !adminRequestConfig) {
        return Promise.resolve([]);
      }
      const request = useAdminCredentials
        ? this.$api.auth.client.get('/users', { ...adminRequestConfig, params }).then(({ data }) => data)
        : this.$api.auth.get('/users', { params });

      return request
        .then(
          (data) => {
            if (data.result) {
              this.users = data.result
                .filter((user) => user.email !== this.user.email)
                .map((user) => {
                  return {
                    id: user.id,
                    name: user.email,
                    fullName: `${user.firstName} ${user.lastName}`,
                    isBanned: Boolean(user.isBanned),
                  };
                });
            }
            return this.users;
          },
          (e) => {
            this.$notify('alert', {
              text: e?.response?.data?.error?.message ?? 'Cannot load users for impersonation',
              variant: 'danger',
              time: 5000,
            });
            this.$router.push({ name: 'dashboard' });
            return [];
          },
        );
    },

    async loginAsEmail(email, { inNewTab = false } = {}) {
      const ids = auth.readFakeLoginIds();
      const userId = ids[email];
      if (!userId) {
        this.$notify('alert', {
          text: `Cannot impersonate ${email}: open Login as once to refresh saved access`,
          variant: 'danger',
          time: 5000,
        });
        return false;
      }
      const targetTab = inNewTab ? window.open('about:blank', '_blank') : null;
      if (inNewTab && !targetTab) {
        return false;
      }
      return this.fakeLoginByUserId(userId, { targetTab, useAdminCredentials: isImpersonating() });
    },

    getRecentlyUsedStorageKey() {
      const fakeLogin = auth.readFakeLogin();
      const adminUser = isImpersonating() ? this.users.find((user) => user.name === fakeLogin.admin) : null;
      return `fake-login-user-select-${adminUser?.id ?? this.user.id}`;
    },

    readRecentlyUsedIds() {
      try {
        const stored = JSON.parse(localStorage.getItem(this.getRecentlyUsedStorageKey()) || '[]');
        if (!Array.isArray(stored)) {
          return [];
        }
        const ids = stored.map((item) => (typeof item === 'object' ? item?.id : item)).filter(Number.isInteger);
        // Older entries are whole user objects kept oldest first; the id list is newest first.
        return stored.some((item) => typeof item === 'object') ? ids.reverse() : ids;
      } catch {
        return [];
      }
    },

    rememberRecentlyUsedUser(userId) {
      // Without the loaded list (login from the menu) the visible history is unknown; keep it as is.
      if (!this.users.some((user) => user.id === userId)) {
        return;
      }

      const next = [userId, ...this.recentlyUsedUsers.map((user) => user.id).filter((id) => id !== userId)].slice(
        0,
        RECENTLY_USED_LIMIT,
      );
      this.recentlyUsedIds = next;
      localStorage.setItem(this.getRecentlyUsedStorageKey(), JSON.stringify(next));
    },

    async getFakeAccess(userId, { useAdminCredentials = false } = {}) {
      const normalize = (res) => {
        return {
          accessToken: res.accessToken || res.access_token,
          refreshToken: res.refreshToken || res.refresh_token,
          impersonateLogin: res.impersonateLogin || res.impersonate_login,
        };
      };
      const handleError = (e) => {
        this.$notify('alert', {
          text: e?.response?.data?.error?.message ?? 'Cannot start impersonation',
          variant: 'danger',
          time: 5000,
        });
        return null;
      };
      if (useAdminCredentials) {
        const admin = this.getAdminUserData();
        const adminRequestConfig = this.getAdminRequestConfig();
        if (!adminRequestConfig) {
          return null;
        }
        return this.$api.auth.client
          .post(`/auth/impersonate-login/${userId}`, { fp: admin.fp }, adminRequestConfig)
          .then(({ data }) => data)
          .then(normalize)
          .catch(handleError);
      }
      return this.$api.auth
        .post(`/auth/impersonate-login/${userId}`, {
          fp: auth.getFp(auth.readUser()),
        })
        .then((res) => normalize(res))
        .catch(handleError);
    },

    async fakeLogin() {
      if (!this.selectedUser.length) {
        return false;
      }
      return this.fakeLoginByUserId(this.selectedUser[0].id, {
        useAdminCredentials: isImpersonating(),
      });
    },

    async fakeLoginInNewTab() {
      if (!this.selectedUser.length) {
        return false;
      }
      const targetTab = window.open('about:blank', '_blank');
      if (!targetTab) {
        return false;
      }
      return this.fakeLoginByUserId(this.selectedUser[0].id, {
        targetTab,
        useAdminCredentials: isImpersonating(),
      });
    },

    async fakeLoginByUserId(userId, { targetTab = null, useAdminCredentials = false } = {}) {
      const fakeAccess = await this.getFakeAccess(userId, { useAdminCredentials });
      if (!fakeAccess?.impersonateLogin || !fakeAccess?.accessToken || !fakeAccess?.refreshToken) {
        targetTab?.close();
        return false;
      }

      const impEmail = fakeAccess.impersonateLogin;
      let userFp = auth.getFp(impEmail) || Date.now().toString();
      const fakeLogin = auth.readFakeLogin();
      const adminEmail = useAdminCredentials ? fakeLogin.admin : auth.readUser() || this.user.email;
      const adminRole = useAdminCredentials ? fakeLogin.adminRole : auth.readActiveRole();
      const sessionFp = (useAdminCredentials ? this.getAdminUserData().fp : auth.getFp(adminEmail)) || '';

      try {
        this.rememberRecentlyUsedUser(userId);

        if (!auth.getFp(impEmail)) {
          auth.saveFp(userFp, impEmail);
        }

        if (targetTab) {
          targetTab.sessionStorage.setItem(
            IMP_TOKENS_KEY,
            JSON.stringify({
              accessToken: fakeAccess.accessToken,
              refreshToken: fakeAccess.refreshToken,
            }),
          );
          targetTab.sessionStorage.setItem(IMP_FP_KEY, sessionFp);
          targetTab.sessionStorage.removeItem('role');
          auth.saveFakeLoginForWindow(targetTab, adminEmail, impEmail, userId, adminRole);
          targetTab.location.href = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          this.$refs['fake-login-modal'].hide();
          return true;
        }

        auth.saveFakeLogin(adminEmail, impEmail, userId, adminRole);

        // Write impersonation tokens to sessionStorage ONLY -- do NOT touch
        // localStorage so other tabs continue operating as the real admin.
        saveImpTokens({
          accessToken: fakeAccess.accessToken,
          refreshToken: fakeAccess.refreshToken,
        });
        saveImpFp(sessionFp);

        // updateCredentials sets the axios Authorization header for this tab.
        // We pass the admin email so the legacy token map key stays consistent;
        // the actual bearer token now comes from sessionStorage via readAccessToken().
        this.$api.updateCredentials({
          email: auth.readUser(),
          token: fakeAccess.accessToken,
          refreshToken: fakeAccess.refreshToken,
        });

        const res = await this.$store.dispatch('fetchUser');
        if (res) {
          const impersonatedAvailableRoles = this.$store.getters.availableRoles ?? [];
          const preferred = pickPreferredRole(impersonatedAvailableRoles);
          this.switchRole(preferred ?? impersonatedAvailableRoles[0]);
          // this.$router.go();
        }

        this.setupUser();
      } catch (e) {
        console.error('Fake login failed:', e);
      }
    },

    getRouteName() {
      switch (this.$store.getters.role) {
        case 'admin':
          return 'users';
        case 'advertiser':
          return 'stats';
        case 'advert-admin':
          return 'stats';
        case 'ad-exchange':
          return 'stats';
        default:
          return 'dashboard';
      }
    },

    setupUser() {
      if (this.stayOnCurrentUrl) {
        // Default to advertiser role — admin-level roles typically lack access
        // to the current (advertiser-scoped) page and would boot the user out.
        const targetRole = (this.availableRoles || []).includes('advertiser')
          ? 'advertiser'
          : auth.readActiveRole();
        this.$store.dispatch('switchRole', targetRole);
        window.location.reload();
        return;
      }

      if (!auth.readActiveRole() && auth.readToken()) {
        const available = this.$store.getters.availableRoles ?? [];
        const preferred = pickPreferredRole(available) ?? available[0];
        if (preferred) {
          this.$store.dispatch('switchRole', preferred);
        }
      } else {
        this.$store.dispatch('switchRole', auth.readActiveRole());
      }

      window.location.href = this.$router.resolve({
        name: this.getRouteName(),
      }).href;
    },

    switchRole(role) {
      this.busEmit('switch-role:start');
      this.$store.dispatch('switchRole', role).then(
        (res) => {
          this.busEmit('switch-role:end');
          if (!this.$route.meta.roles) {
            return res;
          }
          return res;
        },
        (e) => e,
      );
    },
  },

  created() {
    const { emit } = useEventBus();
    this.busEmit = emit;
    if (!this.stayOnCurrentUrl && !isImpersonating()) {
      this.fetchUsers();
    }
  },
};
</script>

<style lang="scss" scoped>
.fake-login-footer {
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 0.75rem;
}

.fake-login-footer__actions {
  display: flex;
  gap: 0.5rem;
}

.users-select__title {
  padding: var(--bs-form-element-padding-y) var(--bs-form-element-padding-x);
  border-top: 1px solid var(--bs-border-color);
  border-bottom: 1px solid var(--bs-border-color);
  color: #747474;
  font-size: 0.75rem;
}

.user-select__email {
  font-weight: 500;
  color: var(--bs-body-color);
  font-size: 0.875rem;
}

.user-select__name {
  font-size: 0.75rem;
  color: #7f7f7f;
}
</style>
